import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Avatar,
  Stack,
  IconButton,
  Tooltip as MuiTooltip,
  Alert,
} from "@mui/material";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Treemap,
} from "recharts";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useDashboardWithPresentation } from "../hooks/useDashboardWithPresentation";
import { useDashboardTabState } from "../hooks/useDashboardTabState";
import {
  DashboardTabChrome,
  DashboardKpiSkeleton,
  DashboardChartSkeleton,
} from "./dashboard/DashboardTabChrome";
import { useTheme, lighten, darken } from "@mui/material/styles";
import { useMutation } from "react-query";
import MetricAiInsightsDialog from "./MetricAiInsightsDialog";
import { getDashboardMetricInsights } from "../api/client";
import { useTranslation } from "react-i18next";
import { limitTrendSeries } from "../utils/chartData";
import DashboardPieChartView from "./dashboard/DashboardPieChartView";
import DashboardRankedBarChart from "./dashboard/DashboardRankedBarChart";
import DashboardChartLegend from "./dashboard/DashboardChartLegend";
import DashboardGroupedChartHint from "./dashboard/DashboardGroupedChartHint";

/* ===== Helpers ===== */
const BR = (x) => (x ?? 0).toLocaleString("pt-BR");

/** Paleta sequencial derivada do tema (usada em linhas/pizza/barras/treemap) */
const buildSeriesFromTheme = (theme) => {
  const p = theme.palette;
  return [
    p.primary.main,
    (p.secondary?.main ?? p.info.main),
    p.warning.main,
    p.success.main,
    p.error.main,
    p.info.main,
    darken(p.primary.main, 0.2),
    lighten(p.primary.main, 0.35),
  ];
};

/* =================== MAPEADOR DA API ===================
   Esperado do backend em /dashboard/clientes/charts:
   {
     cards: {
       totalClientes, novosPeriodo, ativosPeriodo, churnPct, recenciaMediaDias
     },
     charts: {
       awarenessDist:    [{ name: "Desconhecido", value, fill? }, ...],
       awarenessTrend:   [{ month:"Jan", desconhecido, consciente, interessado, engajado, cliente }, ...],
       demographics:     [{ category:"26-35", value }, ...],
       geography:        [{ name:"SP", value }, ...],
       gruposTreemap:    [{ name:"Consciente › Engajado", size, fill? }, ...]
     }
   }
======================================================== */
function mapClientesCharts(api) {
  const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
  const cards = {
    totalClientes: safeNum(api?.cards?.totalClientes),
    novosPeriodo: safeNum(api?.cards?.novosPeriodo),
    ativosPeriodo: safeNum(api?.cards?.ativosPeriodo),
    churnPct: String(api?.cards?.churnPct ?? "0.0"),
    recenciaMediaDias: safeNum(api?.cards?.recenciaMediaDias),
  };

  const charts = {
    awarenessDist: Array.isArray(api?.charts?.awarenessDist) ? api.charts.awarenessDist : [],
    awarenessTrend: Array.isArray(api?.charts?.awarenessTrend) ? api.charts.awarenessTrend : [],
    awarenessFunnel: Array.isArray(api?.charts?.awarenessFunnel) ? api.charts.awarenessFunnel : [],
    ltvByStage: Array.isArray(api?.charts?.ltvByStage) ? api.charts.ltvByStage : [],
    avgDaysInStage: Array.isArray(api?.charts?.avgDaysInStage) ? api.charts.avgDaysInStage : [],
    demographics: Array.isArray(api?.charts?.demographics) ? api.charts.demographics : [],
    geography: Array.isArray(api?.charts?.geography) ? api.charts.geography : [],
    gruposTreemap: Array.isArray(api?.charts?.gruposTreemap) ? api.charts.gruposTreemap : [],
  };

  return { cards, charts };
}

/* ====================== UI AUXILIARES ====================== */
const KpiCard = ({ icon, title, value, color, sub }) => (
  <Card>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ color }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const CustomPieTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        p: 1.2,
        borderRadius: 1,
        boxShadow: "0 0 10px rgba(0,0,0,.2)",
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold">
        {p.name}
      </Typography>
      <Typography variant="body2">Percentual: {BR(p.value)}%</Typography>
    </Box>
  );
};

function CustomTreemapContent(props) {
  const { x, y, width, height, name, size, fill } = props;
  if (width < 60 || height < 28) {
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{ fill, stroke: "#fff" }}
      />
    );
  }
  const short =
    String(name || "").length > 36 ? String(name).slice(0, 36) + "…" : name;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        style={{ fill, stroke: "#fff" }}
      />
      <text x={x + 6} y={y + 18} fill="#111" fontSize={12}>
        {short}
      </text>
      <text x={x + 6} y={y + 34} fill="#333" fontSize={11}>
        {BR(size)} clientes
      </text>
    </g>
  );
}

/* ======================= COMPONENTE ======================= */
const CustomerAwareness = ({
  start, // "YYYY-MM-DD"
  end, // "YYYY-MM-DD"
  ids_grupos = [], // ex.: grupos/níveis de consciência (clientesgrupos)
  search = "",
  ufs = [],
  cidades = [],
  tags = [],
  ids_potenciais = [],
  date_field = "cadastro",
  all_period = false,
  presentationSnapshot = null,
  readOnly = false,
}) => {
  const theme = useTheme();
  const { t: tSettings } = useTranslation("settings");
  const previewPendingMessage = tSettings("dashboardPage.shared.previewSectionPending");
  const chartOthersLabel = tSettings("dashboardPage.shared.chartOthers", "Outros");
  const SERIES = React.useMemo(() => buildSeriesFromTheme(theme), [theme]);

  // Cores para KPIs vindas do tema (mantém semântica dos nomes)
  const COLORS = React.useMemo(
    () => ({
      primary: theme.palette.primary.main,
      accent: theme.palette.secondary?.main ?? theme.palette.info.main,
      warm: theme.palette.warning.main,
      warn: theme.palette.error.main,
      ok: theme.palette.success.main,
    }),
    [theme]
  );

  /* --------- Hook de dados --------- */
  const chartsQ = useDashboardWithPresentation(
    "clientes",
    "charts",
    {
      start,
      end,
      ids_grupos,
      search,
      ufs,
      cidades,
      tags,
      ids_potenciais,
      date_field,
      all_period: all_period ? 1 : undefined,
      type: "all",
    },
    // Preview só quando o full report for deferred (202) — evita 2× scan no banco em paralelo.
    { select: mapClientesCharts, staleTime: 900_000, enabled: true, alwaysPreview: false },
    presentationSnapshot
  );

  const tab = useDashboardTabState(chartsQ, {
    registerShare: !readOnly && !presentationSnapshot,
  });
  const loading = tab.showSkeletons;
  const isPreview = tab.isPreview;

  /* --------- Derivações --------- */
  const totalClientes = tab.data?.cards?.totalClientes ?? 0;
  const novosPeriodo = tab.data?.cards?.novosPeriodo ?? 0;
  const churnPct = tab.data?.cards?.churnPct ?? "0.0";
  const recenciaMediaDias = tab.data?.cards?.recenciaMediaDias ?? 0;

  const awarenessDist = tab.data?.charts?.awarenessDist ?? [];
  const awarenessTrend = tab.data?.charts?.awarenessTrend ?? [];
  const awarenessFunnel = tab.data?.charts?.awarenessFunnel ?? [];
  const ltvByStage = tab.data?.charts?.ltvByStage ?? [];
  const avgDaysInStage = tab.data?.charts?.avgDaysInStage ?? [];
  const demographics = tab.data?.charts?.demographics ?? [];
  const geography = tab.data?.charts?.geography ?? [];
  const gruposTreemap = tab.data?.charts?.gruposTreemap ?? [];

  // Linhas dinâmicas do gráfico de tendência (limita séries quando há muitas categorias)
  const trendPrepared = React.useMemo(
    () =>
      limitTrendSeries(awarenessTrend, {
        maxSeries: 8,
        monthKey: "month",
        othersLabel: chartOthersLabel,
      }),
    [awarenessTrend, chartOthersLabel]
  );
  const trendKeys = trendPrepared.keys;
  const trendChartData = trendPrepared.data;

  // **Normaliza cores dos datasets para o tema**
  const awarenessDistC = React.useMemo(
    () =>
      awarenessDist.map((d, i) => ({
        ...d,
        fill: d.fill || SERIES[i % SERIES.length],
      })),
    [awarenessDist, SERIES]
  );

  const geographyC = React.useMemo(
    () =>
      geography.map((d, i) => ({
        ...d,
        fill: d.fill || SERIES[i % SERIES.length],
      })),
    [geography, SERIES]
  );

  const gruposTreemapC = React.useMemo(
    () =>
      gruposTreemap.map((d, i) => ({
        ...d,
        fill: d.fill || SERIES[i % SERIES.length],
      })),
    [gruposTreemap, SERIES]
  );

  const [metricDialogOpen, setMetricDialogOpen] = React.useState(false);
  const [metricDialogMetric, setMetricDialogMetric] = React.useState(null);
  const [metricDialogName, setMetricDialogName] = React.useState("");
  const [metricDialogLabel, setMetricDialogLabel] = React.useState("");
  const [metricDialogData, setMetricDialogData] = React.useState(null);
  const [metricDialogError, setMetricDialogError] = React.useState(null);
  const [metricDialogHuman, setMetricDialogHuman] = React.useState("");
  const [metricDialogBasePayload, setMetricDialogBasePayload] = React.useState(null);
  const lastCfgRef = React.useRef(null);

  const metricInsightsMutation = useMutation(
    async ({ metric, metricName, metricDefinition, metricValue, metricHuman }) => {
      const payload = {
        metric,
        metricName,
        metricDefinition,
        metricValue,
        metricHuman,
        period: { start, end },
        summaryContext: {
          totalClientes,
          churnPct,
          recenciaMediaDias,
        },
        filters: {
          ids_grupos,
          search,
          ufs,
          cidades,
          tags,
          ids_potenciais,
          date_field,
          all_period,
        },
      };
      return getDashboardMetricInsights("clientes", payload);
    },
    {
      onSuccess: (data) => {
        setMetricDialogData(data || null);
        setMetricDialogError(null);
      },
      onError: (err) => {
        const msg =
          (typeof err?.message === "string" && err.message) ||
          (typeof err?.error === "string" && err.error) ||
          "Não foi possível gerar a explicação da IA no momento.";
        setMetricDialogError(msg);
      },
    }
  );

  const openAi = (cfg) => {
    setMetricDialogMetric(cfg.metric);
    setMetricDialogName(cfg.metricName || "");
    setMetricDialogLabel(cfg.metricLabel || "");
    setMetricDialogHuman(cfg.metricHuman || "");
    setMetricDialogBasePayload(cfg);
    setMetricDialogOpen(true);
    setMetricDialogData(null);
    setMetricDialogError(null);
    lastCfgRef.current = cfg;
    metricInsightsMutation.mutate(cfg);
  };

  const AiBtn = ({ cfg }) => {
    if (readOnly) return null;
    return (
      <MuiTooltip title="Explicar com IA">
        <IconButton size="small" onClick={() => openAi(cfg)} aria-label="Explicar com IA">
          <AutoAwesomeIcon fontSize="inherit" />
        </IconButton>
      </MuiTooltip>
    );
  };

  return (
    <Box>
      <DashboardTabChrome
        isDeferred={tab.isDeferred}
        isPreview={tab.isPreview}
        isInitialLoading={tab.showSkeletons}
        errorMessage={tab.errorMessage}
        previewPendingMessage={previewPendingMessage}
      />
      {/* ===== KPIs ===== */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <Skeleton height={118} />
          ) : (
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: COLORS.primary, width: 44, height: 44 }}>
                    <PeopleAltIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                        Total de Clientes
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "total_clientes",
                          metricName: "Total de Clientes",
                          metricDefinition: "Quantidade total de clientes cadastrados/ativos na base para o período e filtros selecionados.",
                          metricValue: totalClientes,
                          metricHuman: BR(totalClientes),
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ color: COLORS.primary }}>
                      {BR(totalClientes)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Base consolidada
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <Skeleton height={118} />
          ) : (
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: COLORS.accent, width: 44, height: 44 }}>
                    <PersonAddAltIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                        Novos no Período
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "novos_periodo",
                          metricName: "Novos no Período",
                          metricDefinition: "Quantidade de novos clientes cadastrados entre as datas selecionadas.",
                          metricValue: novosPeriodo,
                          metricHuman: BR(novosPeriodo),
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ color: COLORS.accent }}>
                      {BR(novosPeriodo)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Cadastrados entre as datas
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <Skeleton height={118} />
          ) : (
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: COLORS.warm, width: 44, height: 44 }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                        Recência Média
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "recencia_media_dias",
                          metricName: "Recência Média",
                          metricDefinition: "Média de dias desde a última compra/contato (quanto menor, mais recente/engajado tende a ser o relacionamento).",
                          metricValue: recenciaMediaDias,
                          metricHuman: `${BR(recenciaMediaDias)} dias`,
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ color: COLORS.warm }}>
                      {BR(recenciaMediaDias)} dias
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Última compra/contato
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {loading ? (
            <Skeleton height={118} />
          ) : (
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: COLORS.warn, width: 44, height: 44 }}>
                    <TrendingDownIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ flex: 1 }} noWrap>
                        Churn (estimado)
                      </Typography>
                      <AiBtn
                        cfg={{
                          metric: "churn_estimado_pct",
                          metricName: "Churn (estimado)",
                          metricDefinition: "Percentual estimado de clientes inativos/perdidos no período e filtros selecionados.",
                          metricValue: churnPct,
                          metricHuman: `${churnPct}%`,
                        }}
                      />
                    </Stack>
                    <Typography variant="h4" sx={{ color: COLORS.warn }}>
                      {churnPct}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Clientes inativos / perdidos
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* ===== Consciência: Distribuição + Tendência ===== */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>Distribuição de Consciência</Typography>
                <AiBtn
                  cfg={{
                    metric: "awareness_dist",
                    metricName: "Distribuição de Consciência",
                    metricDefinition: "Distribuição dos clientes por nível de consciência no período e filtros selecionados.",
                    metricValue: awarenessDistC,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <DashboardPieChartView
                  data={awarenessDist}
                  series={SERIES}
                  height={300}
                  maxSlices={8}
                  othersLabel={chartOthersLabel}
                  tooltipContent={<CustomPieTooltip />}
                  getFill={(i, item) => item?.fill || SERIES[i % SERIES.length]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Evolução dos Níveis de Consciência
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "awareness_trend",
                    metricName: "Evolução dos Níveis de Consciência",
                    metricDefinition: "Tendência mensal da base por nível de consciência, útil para avaliar aquisição, nutrição e conversão ao longo do tempo.",
                    metricValue: awarenessTrend,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", height: 300 }}>
                  {trendPrepared.grouped ? (
                    <DashboardGroupedChartHint
                      shown={trendKeys.length}
                      total={trendPrepared.totalSeries}
                    />
                  ) : null}
                  <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        {trendKeys.map((k, i) => (
                          <Line
                            key={k}
                            type="monotone"
                            dataKey={k}
                            name={k}
                            stroke={SERIES[i % SERIES.length]}
                            dot={false}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                  <DashboardChartLegend
                    items={trendKeys.map((key) => ({ key, name: key }))}
                    getColor={(i) => SERIES[i % SERIES.length]}
                    maxHeight={88}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {isPreview ? null : (
      <>
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Funil de conscientização (Schwartz)
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "awareness_funnel",
                    metricName: "Funil de conscientização (Schwartz)",
                    metricDefinition:
                      "Volume de clientes por estágio do funil de conscientização de Schwartz, usado para priorizar conteúdo e ofertas por maturidade.",
                    metricValue: awarenessFunnel,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={280} />
              ) : (
                <DashboardRankedBarChart
                  data={awarenessFunnel}
                  categoryKey="stage"
                  valueKey="total"
                  valueName="Clientes"
                  fill={SERIES[1]}
                  height={280}
                  maxCategories={15}
                  othersLabel={chartOthersLabel}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  LTV médio por estágio
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "ltv_by_stage",
                    metricName: "LTV médio por estágio",
                    metricDefinition:
                      "Lifetime value médio dos clientes em cada estágio de conscientização, indicando onde o valor se concentra e oportunidades de upsell.",
                    metricValue: ltvByStage,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={280} />
              ) : (
                <DashboardRankedBarChart
                  data={ltvByStage}
                  categoryKey="stage"
                  valueKey="ltvMedio"
                  valueName="LTV médio"
                  fill={SERIES[2]}
                  height={280}
                  maxCategories={15}
                  othersLabel={chartOthersLabel}
                  formatValue={(v) => `R$ ${BR(v)}`}
                  formatTooltip={(v) => `R$ ${BR(v)}`}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== Perfil (Demografia) + Geografia ===== */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Perfil Demográfico
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "demographics",
                    metricName: "Perfil Demográfico",
                    metricDefinition: "Distribuição de clientes por faixa/segmento demográfico, usada para ajustar comunicação e ofertas.",
                    metricValue: demographics,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={demographics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" name="Clientes" fill={SERIES[0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Distribuição Geográfica
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "geography",
                    metricName: "Distribuição Geográfica",
                    metricDefinition: "Distribuição de clientes por região/UF/cidade, útil para logística, atendimento e campanhas segmentadas.",
                    metricValue: geographyC,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <DashboardPieChartView
                  data={geography}
                  series={SERIES}
                  height={300}
                  maxSlices={8}
                  othersLabel={chartOthersLabel}
                  getFill={(i, item) => item?.fill || SERIES[i % SERIES.length]}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== Grupos/Segmentos (Treemap) ===== */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">
                  Grupos & Níveis de Consciência (hierarquia)
                </Typography>
                <AiBtn
                  cfg={{
                    metric: "grupos_treemap",
                    metricName: "Grupos & Níveis de Consciência (hierarquia)",
                    metricDefinition: "Treemap de segmentos/grupos de clientes, indicando concentração por grupo e oportunidades de segmentação.",
                    metricValue: gruposTreemapC,
                    metricHuman: "",
                  }}
                />
              </Stack>
              {loading ? (
                <Skeleton variant="rectangular" height={320} />
              ) : gruposTreemapC?.length ? (
                <ResponsiveContainer width="100%" height={320}>
                  <Treemap
                    data={gruposTreemapC}
                    dataKey="size"
                    stroke="#fff"
                    content={<CustomTreemapContent />}
                  />
                </ResponsiveContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Sem dados para o período/filtro selecionado.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {!readOnly ? (
      <MetricAiInsightsDialog
        open={metricDialogOpen}
        metric={metricDialogMetric}
        metricName={metricDialogName}
        metricLabel={metricDialogLabel}
        metricHuman={metricDialogHuman}
        period={{ start, end }}
        loading={metricInsightsMutation.isLoading}
        error={metricDialogError}
        data={metricDialogData}
        scope="clientes"
        basePayload={metricDialogBasePayload}
        onClose={() => setMetricDialogOpen(false)}
      />
      ) : null}
    </Box>
  );
};

export default CustomerAwareness;
